import { useTranslations } from "next-intl";
import {
  ChangeEventHandler,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from "react";
import * as EmailValidator from "email-validator";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import BackToLogin from "./BackToLoginButton";
import useEnhancedAsync from "src/hooks/useEnhancedAsync";
import BlackSpinIcon from "src/components/icon/BlackSpinIcon";
import InlineInput from "src/components/inline-input/InlineInput";
import ThemeButton from "src/components/button/ThemeButton";
import { ServerApiClient } from "src/apis/server-api-client";
import HttpStatusCode from "src/types/api-client/HttpStatusCode";
import { AnimatePresence } from "framer-motion";
import ErrorMessage from "src/components/alerts/ErrorMessage";
import SuccessMessage from "src/components/alerts/SuccessMessage";

type FormFields = {
  email: string;
};

interface FormErrors {
  email: string;
}

function validateForm(
  formFields: FormFields,
  setFormErrors: Dispatch<SetStateAction<FormErrors>>,
  t: (string: string) => string
) {
  // email
  let emailError = "";
  if (!formFields.email) {
    emailError = t("email_is_required");
  } else if (!EmailValidator.validate(formFields.email)) {
    emailError = t("validate_email");
  }

  setFormErrors((prevState) => ({
    ...prevState,
    email: emailError,
  }));

  return ![emailError].some(Boolean);
}

const serverApiClient = new ServerApiClient();

export default function ForgotPasswordForm() {
  const t = useTranslations("ForgotPassword");

  const [forgotPasswordErrorStatus, setForgotPasswordErrorStatus] =
    useState("");
  const [credentials, setCredentials] = useState<FormFields>({
    email: "",
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({
    email: "",
  });
  const [isFormSubmittedOnce, setIsFormSubmittedOnce] = useState(false);

  const handleChange: ChangeEventHandler<HTMLInputElement> = (event) => {
    const { name, value } = event.target;
    setCredentials((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const [handleSubmit, loadingSubmission, submitError, _, submitSuccess] =
    useEnhancedAsync(async (e) => {
      e.preventDefault();
      setForgotPasswordErrorStatus("");
      setIsFormSubmittedOnce(true);

      /**
       * Step 1: validate inputs
       */
      const isFormValid = validateForm(credentials, setFormErrors, t);

      if (!isFormValid) {
        return;
      }

      /**
       * Step 2: Send a forgot password request
       */
      const forgotPasswordResult = await serverApiClient.forgotPassword(
        credentials.email
      );
      if (forgotPasswordResult.isErr()) {
        if (
          forgotPasswordResult.error.errorStatus === HttpStatusCode.NOT_FOUND
        ) {
          setForgotPasswordErrorStatus(t("user_not_found"));
        } else {
          setForgotPasswordErrorStatus(t("something_went_wrong"));
        }

        throw new Error(JSON.stringify(forgotPasswordResult.error));
      }
    });

  // effects
  useEffect(
    function validateInputsOnChange() {
      if (isFormSubmittedOnce) {
        validateForm(credentials, setFormErrors, t);
      }
    },
    [isFormSubmittedOnce, credentials]
  );

  return (
    <form
      className="rounded-sm border border-gray-200 p-8 text-center shadow-sm"
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="mb-5 inline-flex rounded-full border-2 border-beige-900 bg-beige-400 p-3.5">
        <LockClosedIcon className="h-8 w-8" />
      </div>
      <div className="space-y-5">
        <header className="space-y-2">
          <h1 className="title-1">{t("forgot_password")}</h1>
          <h2 className="font-medium text-gray-500">
            {t("reset_instructions")}
          </h2>
        </header>
        <div>
          <InlineInput
            inputId="forgot_password_email"
            type="email"
            name="email"
            label={t("email")}
            value={credentials.email}
            onChange={handleChange}
            errorMessage={formErrors.email}
          />
        </div>
        <div className="space-y-2">
          <ThemeButton
            id="reset_password_button"
            type="submit"
            disabled={loadingSubmission}
            className="w-full"
          >
            {loadingSubmission && <BlackSpinIcon className="-ms-1 me-3" />}
            <span>{t("reset_password")}</span>
          </ThemeButton>

          <BackToLogin />

          <AnimatePresence>
            {/* Error message */}
            {forgotPasswordErrorStatus && (
              <ErrorMessage message={forgotPasswordErrorStatus} />
            )}

            {/* Success message */}
            {submitSuccess && (
              <SuccessMessage message={t("password_reset_link_sent")} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </form>
  );
}
